import React, { PureComponent, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { HotKeys } from 'react-hotkeys';
import { onClickOutsideHOC } from '@eventbrite/legacy-eds.utils/click';
import { HIDE_CHILD, SHOW_CHILD } from '@eventbrite/legacy-eds.hot-keys';
import { ACTION_KEY_MAP } from './hotKeys';
import { Tooltip } from './Tooltip';
import styles from './withTooltip.module.scss';

interface TooltipProps {
    tooltipId: string;
    tooltipText: string | React.ReactNode;
    tooltipArrowLocation?: string;
    tooltipStyle?: string;
    hideTooltipOnMouseLeave?: boolean;
    keepTooltipOpen?: boolean;
    manuallyToggleTooltip?: boolean;
    renderOnMount?: boolean;
    onShowTooltip?: () => void;
    wrapperComponent?: string;
    isOpened?: boolean;
    enhancedA11y?: boolean;
    __containerClassName?: string;
}

type Props<P = {}> = TooltipProps & P;

/**
 * withTooltip is an HOC that provides the wrappedComponent with a tooltip.
 *
 * To use the HOC, wrap the component like so:
 *   let ButtonWithTooltip = withTooltip(Button);
 *
 * Then, provide the following props through the wrappedComponent
 *
 * e.g. <ButtonWithTooltip tooltipText="A button"> This is Button Text </ButtonWithTooltip>
 * 1) tooltipText             - required
 * 2) arrowLocation           - optional
 * 3) tooltipStyle            - optional
 * 4) hideTooltipOnMouseLeave - optional
 * 5) keepTooltipOpen         - optional
 * 6) manuallyToggleTooltip   - optional
 * 7) renderOnMount           - optional
 * 8) onShowTooltip           - optional
 * 9) enhancedA11y            - optional (set to true to enable enhanced accessibility features)
 *
 * @summary An HOC that provides the wrappedComponent with an accompanying tooltip
 */
export const withTooltip = <P extends object>(Component: React.ComponentType<P>) =>
    class WithTooltipWrapper extends PureComponent<Props<P>> {
        _rootNode: HTMLElement | null = null;
        _mouseOverNode = false;
        _nodeFocused = false;

        state = {
            targetNode: null,
            element: null as HTMLElement | null,
            shouldShowTooltip: true,
            isTooltipVisible: false,
            isFocused: false,
        };

        hotKeyHandlers = {
            [HIDE_CHILD]: () => this._handleKeyPressedHide(),
            [SHOW_CHILD]: (e) => this._handleFocus(e),
        };

        componentDidUpdate(_prevProps, prevState) {
            if (prevState.element === null && !!this.state.element && this.props.renderOnMount) {
                this.show();
            }

            if (_prevProps.isOpened !== this.props.isOpened) {
                this._updateShowTooltip();
            }
        }

        componentDidMount() {
            this._updateShowTooltip();
        }

        _updateShowTooltip() {
            // * Checking for truthy or falsy is not an option. Could be undefined.
            if (this.props.isOpened === false) {
                this.setState({ shouldShowTooltip: false });
            }
        }

        _handleMouseOver = (e) => {
            if (this.props.manuallyToggleTooltip) {
                return;
            }

            this.setState({
                shouldShowTooltip: true,
            });

            this._mouseOverNode = true;

            this._handleShow(e);
        };

        _handleMouseLeave = () => {
            if (this.props.keepTooltipOpen || this.props.manuallyToggleTooltip) {
                return;
            }

            this._mouseOverNode = false;

            if (
                (!this._nodeFocused || this.props.hideTooltipOnMouseLeave) &&
                !this._mouseOverNode
            ) {
                this._handleHide();
            }
        };

        _handleFocus = (e) => {
            this._nodeFocused = true;
            if (this.props.enhancedA11y) {
                this.setState({ isFocused: true });
            }
            this._handleShow(e);
        };

        _handleKeyPressedHide = () => {
            if (this._nodeFocused || this._mouseOverNode) {
                this._nodeFocused = true;
                this._mouseOverNode = false;
                this._handleHide();
            }
        };

        _handleReset = () => {
            if (this.props.keepTooltipOpen) {
                this._nodeFocused = false;
                this._mouseOverNode = false;
                this._handleHide();
            }
            if (this._nodeFocused || this._mouseOverNode) {
                this._nodeFocused = false;
                this._mouseOverNode = false;
                this._handleHide();
            }
        };

        _handleBlur = () => {
            if (this.props.manuallyToggleTooltip) {
                return;
            }

            this._nodeFocused = false;
            if (this.props.enhancedA11y) {
                this.setState({ isFocused: false });
            }

            if (!this._nodeFocused && !this._mouseOverNode) {
                this._handleHide();
            }
        };

        _handleKeyDown = (e) => {
            if (this.props.enhancedA11y && e.key === 'Escape' && this.state.isTooltipVisible) {
                this._handleHide();
                if (this.state.element) {
                    this.state.element.focus();
                }
            }
        };

        show = () => {
            this._handleShow({ currentTarget: this.state.element });
        };

        _handleShow = (e) => {
            this.setState({
                targetNode: e.currentTarget,
                isTooltipVisible: true,
            });
            this.props?.onShowTooltip?.();
        };

        hide = () => {
            this._handleHide();
        };

        _handleHide = () => {
            this.setState({
                targetNode: null,
                isTooltipVisible: false,
            });
        };

        _getTooltipTextContent = () => {
            const { tooltipText } = this.props;
            if (typeof tooltipText === 'string') {
                return tooltipText;
            }
            return '';
        };

        render() {
            const { targetNode, shouldShowTooltip, isTooltipVisible } = this.state;
            const {
                tooltipId,
                tooltipText,
                tooltipArrowLocation,
                tooltipStyle,
                wrapperComponent,
                enhancedA11y,
                __containerClassName,
                // Extract all tooltip props to exclude them from component props
                tooltipId: _tooltipId,
                tooltipText: _tooltipText,
                tooltipArrowLocation: _tooltipArrowLocation,
                tooltipStyle: _tooltipStyle,
                hideTooltipOnMouseLeave: _hideTooltipOnMouseLeave,
                keepTooltipOpen: _keepTooltipOpen,
                manuallyToggleTooltip: _manuallyToggleTooltip,
                renderOnMount: _renderOnMount,
                onShowTooltip: _onShowTooltip,
                wrapperComponent: _wrapperComponent,
                isOpened: _isOpened,
                enhancedA11y: _enhancedA11y,
                __containerClassName: _containerClassName,
                ...componentProps
            } = this.props;

            const tooltipClasses = classNames(styles.tooltipHocWrapper, __containerClassName);
            let describedBy;

            // We don't want `aria-describedby` on the div when no tooltip is
            // present
            if (targetNode) {
                describedBy = tooltipId;
            }

            return (
                <HotKeys
                    component={wrapperComponent}
                    keyMap={ACTION_KEY_MAP}
                    handlers={this.hotKeyHandlers}
                >
                    <ToolTipWrapper
                        tooltipClasses={tooltipClasses}
                        onMouseOver={this._handleMouseOver}
                        onMouseLeave={this._handleMouseLeave}
                        onFocus={this._handleFocus}
                        onBlur={this._handleBlur}
                        onKeyDown={enhancedA11y ? this._handleKeyDown : undefined}
                        onClickOutside={this._handleReset}
                        describedBy={describedBy}
                        useTabIndex={enhancedA11y}
                        onElementReady={(element) => {
                            this.setState({
                                element,
                            });
                        }}
                    >
                        <Component {...(componentProps as P)} />

                        <Tooltip
                            tooltipId={tooltipId}
                            targetNode={shouldShowTooltip ? targetNode : null}
                            arrowLocation={tooltipArrowLocation}
                            style={tooltipStyle}
                        >
                            {tooltipText}
                        </Tooltip>

                        {/* Hidden element for screen readers - only render if enhancedA11y is enabled */}
                        {enhancedA11y && isTooltipVisible && (
                            <span
                                id={tooltipId}
                                className={styles.tooltipSrOnly}
                                aria-hidden="false"
                            >
                                {tooltipText}
                            </span>
                        )}
                    </ToolTipWrapper>
                </HotKeys>
            );
        }
    };

const ToolTipWrapper = onClickOutsideHOC(
    ({
        tooltipClasses,
        onMouseOver,
        onMouseLeave,
        onFocus,
        onClick,
        onBlur,
        onKeyDown,
        describedBy,
        children,
        useTabIndex,
        onElementReady,
    }) => {
        const element = useRef(null);

        useEffect(() => {
            let unmounted = false;

            if (element.current && !unmounted) {
                onElementReady(element.current);
            }

            return () => {
                unmounted = true;
            };
            // eslint-disable-next-line
        }, [element.current, onElementReady]);

        return (
            /* Accessibility: This component does have key events, but not picked up by the linter becasue of the HOC */
            /* Ideally this component would be a button element but as it is a HOC, that has already been relased we can't do this. e.g. copy button with tooltip */
            <div
                ref={element}
                className={tooltipClasses}
                onMouseOver={onMouseOver}
                onMouseLeave={onMouseLeave}
                onFocus={onFocus}
                onClick={onClick}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                // `aria-describedby` is based on the <button> tag and not
                // with the component because in cases where component has
                // `aria-hidden` (i.e. <i> ), we still want `aria-describedby`
                // to be present and corresponds to the tooltip id
                aria-describedby={describedBy}
                tabIndex={useTabIndex ? 0 : undefined}
                data-spec="tooltip-wrapper"
            >
                {children}
            </div>
        );
    },
);
